import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useEditMode } from "@/contexts/CmsContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ensureTextLoaded,
  subscribeText,
  getCachedText,
  stageText,
  unhideDocumentForCms,
  hasAnyCachedText,
} from "@/lib/slotCache";
import { slotFromTextElement, slotFromPlaceholderElement } from "@/lib/autoSlot";

// ────────────────────────────────────────────────────────────────────────
// AutoTextEditor — global engine that makes every text node on the site
// editable from "Edit Text" mode without touching individual components.
//
// • Scans the DOM (MutationObserver) for text-bearing leaf elements.
// • Generates a stable slot id from each element's DOM position.
// • Applies persisted overrides on first paint and after every nav.
// • In edit mode, sets contentEditable on the element with a soft ring.
// • Saves on blur — mirrors the same text across EN / FR / ES via the
//   `persistText()` mirror logic so the same content block stays in
//   sync across all language versions of the website.
//
// Opt-out:
//   • Wrap in <EditableText> already      → auto-skipped (data-cms-text-slot)
//   • data-no-cms-edit="true" (any ancestor)
//   • data-no-cms-text="true"             (text-only opt-out)
// ────────────────────────────────────────────────────────────────────────

const TEXT_TAGS = new Set([
  "H1", "H2", "H3", "H4", "H5", "H6",
  "P", "SPAN", "A", "BUTTON", "LABEL", "LI",
  "BLOCKQUOTE", "FIGCAPTION", "Q",
  "EM", "STRONG", "SMALL", "B", "I", "U", "S",
  "DT", "DD", "TD", "TH",
  "SUMMARY", "CAPTION", "TIME", "MARK", "CITE", "ABBR", "ADDRESS",
  "DIV", "ARTICLE", "ASIDE", "SECTION", "HEADER", "FOOTER", "NAV", "MAIN",
]);

// Divs are usually layout containers, NOT text. Only treat a div as a
// text candidate if it visually contains text directly (no nested
// text-tag children). This prevents wrapping huge sections.
const isInsideManagedText = (el) =>
  !!el.closest && (el.closest("[data-cms-text-slot]") || el.closest("[data-editable-text-on]"));
const isOptedOut = (el) =>
  !!el.closest &&
  (el.closest('[data-no-cms-edit="true"]') ||
    el.closest('[data-no-cms-text="true"]'));
const isFormInput = (el) =>
  !!el.closest && el.closest("input, textarea, select");
// Skip Leaflet maps, chatbot widget internals, edit-mode controls themselves.
const isOwnUi = (el) =>
  !!el.closest &&
  (el.closest(".leaflet-container") ||
    el.closest(".auto-image-edit-btn") ||
    el.closest("[data-cms-ui]") ||
    el.closest("[data-edit-allow]"));

const MAX_TEXT_LEN = 20000;

// ── Whitespace normalisation ────────────────────────────────────────────
// Multi-line-aware normaliser used everywhere in the auto-text pipeline
// (save handler, override compare, baseline capture, cache re-hydrate).
// It preserves `\n` verbatim so operators can paste multi-paragraph copy
// from ChatGPT / Word / Google Docs / Notion, while still collapsing
// noisy horizontal whitespace (indentation, double spaces, NBSPs) and
// trimming outer blank lines. Runs of 3+ consecutive newlines are
// collapsed to a paragraph break (2 newlines) so we never grow the
// document indefinitely from repeated pastes.
const normalizeCmsText = (raw) => {
  if (raw === null || raw === undefined) return "";
  const s = String(raw);
  return s
    .replace(/\u00A0/g, " ")           // non-breaking space → normal space
    .replace(/\r\n/g, "\n")            // CRLF → LF
    .replace(/\r/g, "\n")              // stray CR → LF
    .replace(/[ \t]+/g, " ")           // collapse runs of spaces/tabs
    .replace(/^[ \t]+|[ \t]+$/gm, "")  // strip per-line edge ws
    .replace(/\n{3,}/g, "\n\n")        // clamp to a single blank line
    .replace(/^\s+|\s+$/g, "");        // trim outer whitespace
};

// Utility: apply the multiline visual affordance (CSS `white-space:
// pre-line`) on any element whose current or persisted text contains a
// real newline. Handled via a data attribute + a scoped CSS rule so we
// can toggle it dynamically as the operator types Enter without
// touching inline styles or the element's own class list.
const setMultilineFlag = (el, hasNewline) => {
  if (!el || el.nodeType !== 1) return;
  if (hasNewline) {
    if (el.dataset.cmsMultiline !== "1") el.dataset.cmsMultiline = "1";
  } else if (el.dataset.cmsMultiline === "1") {
    delete el.dataset.cmsMultiline;
  }
};

// ── Per-element Undo/Redo history ───────────────────────────────────────
// The browser's native contentEditable undo stack only tracks changes
// that go through its own text-input pipeline (keystrokes, native
// paste, execCommand). We use direct DOM mutation for pastes (to
// preserve `\n` verbatim and avoid Chrome's plaintext-only quirks),
// which the browser cannot undo. This tiny history map fills the gap:
// every meaningful edit pushes a snapshot; Ctrl/⌘+Z restores the
// previous snapshot; Ctrl/⌘+Shift+Z (or Ctrl+Y) walks back forward.
//
// Snapshots are per-element (WeakMap so the entry is GC'd when the
// element leaves the DOM). Each snapshot stores the plain-text value
// AND the caret offset so undo restores both the content and the
// user's editing position — no jarring jump back to position 0.
const HISTORY_CAP = 100;
const historyMap = new WeakMap();

const getSelectionOffset = (el) => {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;
  const pre = range.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.startContainer, range.startOffset);
  return pre.toString().length;
};

const setCaretOffset = (el, offset) => {
  if (offset == null) return;
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel) return;
  let remaining = offset;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  let target = null;
  let targetOffset = 0;
  while (node) {
    const len = node.nodeValue.length;
    if (remaining <= len) {
      target = node;
      targetOffset = remaining;
      break;
    }
    remaining -= len;
    node = walker.nextNode();
  }
  const range = document.createRange();
  if (target) {
    range.setStart(target, Math.min(targetOffset, target.nodeValue.length));
  } else {
    range.selectNodeContents(el);
    range.collapse(false);
  }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

// Read/write the DOM as text — matches what `applyOverrides` and
// `onPaste` already produce, so undo/redo restore a clean, canonical
// DOM without stranded `<br>` or wrapper spans.
//
// PRESERVES REACT'S FIBER TREE INTEGRITY:
//   • Element children (SVG icons, images, decorative <em>/<strong>)
//     stay in place — wiping them strands React's fibers on now-orphan
//     nodes and crashes the next unmount with `NotFoundError`.
//   • The FIRST existing text node is MUTATED in place via its `.data`
//     setter rather than being removed and replaced. React's fiber
//     ref still points to the same Text object so React's reconciler
//     can still find and clean up that node correctly on unmount.
//     Trailing text nodes are removed (rare — happens after paste).
// If no text node exists, a fresh Text is inserted before the first
// element child (so text renders left of icons, matching the common
// `text + icon` CTA pattern like "Book now →").
const setElementText = (el, text) => {
  const originalChildren = Array.from(el.childNodes);
  let firstTextNode = null;
  const trailingTextNodes = [];
  for (const node of originalChildren) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (firstTextNode === null) {
        firstTextNode = node;
      } else {
        trailingTextNodes.push(node);
      }
    }
  }
  // Drop any secondary text nodes that would produce stray characters
  // after the primary write below. React never renders two adjacent
  // text nodes for a single JSX expression, so removing these is safe.
  for (const node of trailingTextNodes) {
    if (node.parentNode === el) el.removeChild(node);
  }
  if (firstTextNode) {
    if (text) {
      if (firstTextNode.data !== text) firstTextNode.data = text;
    } else if (firstTextNode.parentNode === el) {
      el.removeChild(firstTextNode);
    }
  } else if (text) {
    // No existing text node — insert one, positioned before the first
    // element child to preserve the visual "text · icon" order.
    const newNode = document.createTextNode(text);
    const firstElemChild = el.firstElementChild;
    if (firstElemChild && firstElemChild.parentNode === el) {
      el.insertBefore(newNode, firstElemChild);
    } else {
      el.appendChild(newNode);
    }
  }
  setMultilineFlag(el, text.includes("\n"));
};

const captureSnapshot = (el) => ({
  text: el.innerText || "",
  caret: getSelectionOffset(el),
});

// Initialise or reset the history for an element to its current state
// as a single-entry stack (index 0). Called on the first focus we see
// after (re-)binding so subsequent edits build off a known baseline.
const ensureHistoryBaseline = (el) => {
  if (!historyMap.has(el)) {
    historyMap.set(el, { stack: [captureSnapshot(el)], index: 0 });
  }
};

// Push a snapshot of the CURRENT DOM state — call AFTER a change has
// been applied so undo(el) restores back to the previous snapshot.
// Debounced by the caller so bursts of keystrokes coalesce into one
// history entry (typical text-editor UX).
const pushHistorySnapshot = (el) => {
  ensureHistoryBaseline(el);
  const h = historyMap.get(el);
  const snap = captureSnapshot(el);
  const cur = h.stack[h.index];
  // Skip identical snapshots (avoids duplicate entries when nothing
  // actually changed between the previous scheduled snapshot and now).
  if (cur && cur.text === snap.text) return;
  // Truncate any "redo tail" — a new edit invalidates the forward path.
  h.stack.length = h.index + 1;
  h.stack.push(snap);
  if (h.stack.length > HISTORY_CAP) {
    h.stack.shift();
  } else {
    h.index += 1;
  }
};

const undoHistory = (el) => {
  const h = historyMap.get(el);
  if (!h || h.index <= 0) return false;
  h.index -= 1;
  const snap = h.stack[h.index];
  setElementText(el, snap.text);
  setCaretOffset(el, snap.caret);
  return true;
};

const redoHistory = (el) => {
  const h = historyMap.get(el);
  if (!h || h.index >= h.stack.length - 1) return false;
  h.index += 1;
  const snap = h.stack[h.index];
  setElementText(el, snap.text);
  setCaretOffset(el, snap.caret);
  return true;
};

const hasNestedTextBearingChild = (el) => {
  // We want to find the leaf text container — the smallest element
  // wrapping just text + decorative icons. If the element contains ANY
  // descendant (not just direct child) that's a text-bearing tag with
  // visible text, this element isn't a leaf and must be skipped.
  // Direct-child-only logic missed wrappers like
  //   <li><article><h3><a>Title</a></h3></article></li>
  // where the only direct child of <li> is <article> (not in TEXT_TAGS),
  // making the whole <li> editable and intercepting mousedown events on
  // every descendant link/button.
  const candidates = el.querySelectorAll(Array.from(TEXT_TAGS).join(","));
  for (const child of candidates) {
    if (child === el) continue;
    const t = (child.textContent || "").replace(/\s+/g, " ").trim();
    if (t) return true;
  }
  return false;
};

// Static (structural) check — doesn't consider whether the element is
// currently contentEditable. Used by the cleanup pass in
// `hydrateInteractivity` so we can tell "is this STILL a valid binding"
// without disqualifying the element because of the contenteditable
// attribute we ourselves set on the previous hydrate.
const isStaticallyCandidate = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (!TEXT_TAGS.has(el.tagName)) return false;
  if (isInsideManagedText(el)) return false;
  if (isOptedOut(el)) return false;
  if (isFormInput(el)) return false;
  if (isOwnUi(el)) return false;

  const text = (el.textContent || "").replace(/\s+/g, " ").trim();
  if (!text) return false;
  if (text.length > MAX_TEXT_LEN) return false;
  if (hasNestedTextBearingChild(el)) return false;

  // Treat structural wrapper tags (DIV, ARTICLE, ASIDE, SECTION,
  // HEADER, FOOTER, NAV, MAIN) as text candidates ONLY when they
  // contain just text (and decorative children like icons / images).
  // This stops the walker from claiming an entire page section and
  // intercepting every nested link.
  const isWrapperTag = new Set([
    "DIV", "ARTICLE", "ASIDE", "SECTION", "HEADER", "FOOTER", "NAV", "MAIN",
  ]);
  if (isWrapperTag.has(el.tagName)) {
    const DECORATIVE_TAGS = new Set([
      "BR", "SVG", "IMG", "PICTURE", "I", "EM", "STRONG", "B", "U", "S", "MARK",
    ]);
    for (const child of el.children) {
      // SVG elements have lowercase tagName (`svg`, `path`, …) in HTML
      // documents because they use XML namespaces — case-insensitive
      // comparison is required, otherwise every `<div>icon + text</div>`
      // pattern (View hotel CTAs, location pills, etc.) silently fails
      // to bind, defeating CMS edit-mode on common UI primitives.
      const tag = (child.tagName || "").toUpperCase();
      if (DECORATIVE_TAGS.has(tag)) continue;
      // Children explicitly opted out of CMS editing are decorative.
      if (child.hasAttribute && (
        child.hasAttribute("data-no-cms-text") ||
        child.hasAttribute("data-no-cms-edit")
      )) continue;
      // Empty wrapper children (no visible text) are decorative too.
      const childText = (child.textContent || "").replace(/\s+/g, " ").trim();
      if (!childText) continue;
      return false;
    }
  }
  return true;
};

const isTextCandidate = (el) => {
  if (!isStaticallyCandidate(el)) return false;
  if (el.isContentEditable) return false;
  return true;
};

// Apply persisted overrides directly on the DOM so the user sees saved
// text even when components render hardcoded strings on first paint.
// When no override exists for a slot, we leave the DOM untouched — the
// component is already rendering its hardcoded i18n default in the
// active language and that is the canonical source.
const applyOverridesToRoot = (root, currentLang) => {
  if (!root) return;
  const selector = Array.from(TEXT_TAGS).join(",");
  const list = root.matches && root.matches(selector)
    ? [root, ...root.querySelectorAll(selector)]
    : root.querySelectorAll
      ? root.querySelectorAll(selector)
      : [];
  list.forEach((el) => {
    if (!isTextCandidate(el) && !el.dataset.cmsAutoText) return;
    // Never overwrite an element the editor is actively typing in.
    // In Edit Mode every bound element is `contentEditable`; rewriting
    // its text node mid-keystroke would erase the user's input and
    // reset the cursor. The CMS cache stays in sync via `stageText` on
    // blur, so skipping here is safe.
    if (el.isContentEditable) return;
    if (!el.dataset.cmsText) {
      el.dataset.cmsText = normalizeCmsText(el.textContent || "");
    }
    const slot = el.dataset.cmsAutoText || slotFromTextElement(el);
    if (!slot) return;
    if (!el.dataset.cmsAutoText) el.dataset.cmsAutoText = slot;
    const stored = getCachedText(slot);
    if (!stored) return;
    const next = stored[currentLang];
    // Allow an explicitly-cleared slot (next === "") to write through —
    // the operator deliberately blanked this text and we must honour
    // that. Only skip when the cache truly has nothing for this lang
    // (undefined / null).
    if (next === undefined || next === null) return;
    const currentNorm = normalizeCmsText(el.innerText || el.textContent || "");
    const nextNorm = normalizeCmsText(next);
    if (currentNorm === nextNorm) return;
    if (next === "") {
      // Removing all text: mutate the first text-node child's `.data`
      // to empty and drop any trailing text nodes, so the element's
      // element children (icons, images) and React's fibers remain
      // intact. See `setElementText` for the full rationale.
      setElementText(el, "");
      return;
    }
    // Multi-line-safe write: mutate the first text node's `.data` in
    // place (preserving React's fiber ref to that node) and keep
    // every element child untouched. Wiping every child via a
    // `firstChild` loop stranded React's fibers on now-orphaned
    // `<svg>` / `<Check>` icons, crashing the next unmount / route
    // change with `NotFoundError: The node to be removed is not a
    // child of this node`.
    setElementText(el, nextNorm);
  });
};

const applyOverrides = (currentLang) => applyOverridesToRoot(document.body, currentLang);

// Apply persisted overrides to form-input placeholders. Slots live in
// the SAME `text_slots` collection as regular visible text — they just
// use a dedicated `text.<page>.ph.<hash>` namespace (see
// `slotFromPlaceholderElement` in `autoSlot.js` and the matching
// `placeholderSlotFor` helper in `bootstrap_text_slots.py`).
//
// This is what makes edits made in `/admin/text` flow onto every
// `<input placeholder="…">` and `<textarea placeholder="…">` on the
// live site without touching the component that owns the input.
const applyPlaceholderOverridesToRoot = (root, currentLang) => {
  if (!root || !root.querySelectorAll) return;
  const selector = "input[placeholder], textarea[placeholder]";
  const list = root.matches && root.matches(selector)
    ? [root, ...root.querySelectorAll(selector)]
    : root.querySelectorAll(selector);
  list.forEach((el) => {
    // Same opt-outs as the visible-text pass — never touch CMS UI's
    // own inputs (search boxes in floating toolbars, etc.).
    if (isOptedOut(el)) return;
    if (isOwnUi(el)) return;
    // Cache the original placeholder once so a CMS override can be
    // reverted (empty slot payload → restore the hardcoded default).
    if (el.dataset.cmsPlaceholder === undefined) {
      el.dataset.cmsPlaceholder = el.getAttribute("placeholder") || "";
    }
    const slot = slotFromPlaceholderElement(el);
    if (!slot) return;
    if (!el.dataset.cmsAutoPh) el.dataset.cmsAutoPh = slot;
    const stored = getCachedText(slot);
    if (!stored) {
      // No CMS override — make sure we're rendering the hardcoded
      // default (in case a previous override was cleared).
      if (el.getAttribute("placeholder") !== el.dataset.cmsPlaceholder) {
        el.setAttribute("placeholder", el.dataset.cmsPlaceholder);
      }
      return;
    }
    const next = stored[currentLang];
    if (next === undefined || next === null) return;
    const current = el.getAttribute("placeholder") || "";
    if (current === next) return;
    el.setAttribute("placeholder", next);
  });
};

const applyPlaceholders = (currentLang) =>
  applyPlaceholderOverridesToRoot(document.body, currentLang);

const AutoTextEditor = () => {
  const { textEditMode } = useEditMode();
  const { lang } = useLanguage();
  const tickRef = useRef(null);
  const subsRef = useRef(new Map()); // slot -> unsubscribe fn
  const fnsRef = useRef({}); // late-bound function handles (break cyclic useCallback deps)

  // Never touch admin pages — those are internal tools, not brand copy.
  const isAdminPath =
    typeof window !== "undefined" &&
    /^\/admin(\/|$)/.test(window.location.pathname || "");

  const tick = useCallback(() => {
    if (tickRef.current) cancelAnimationFrame(tickRef.current);
    tickRef.current = requestAnimationFrame(() => {
      applyOverrides(lang);
      applyPlaceholders(lang);
      if (fnsRef.current.hydrate) fnsRef.current.hydrate();
      // Unhide AFTER overrides have been written to the DOM but BEFORE
      // the browser paints this frame — so cold-cache visitors never
      // see a single frame of hardcoded fallback text. Crucially, only
      // unhide when the slot cache actually has data: if we unhide
      // before the fetch resolves the user would see the hardcoded
      // defaults (since applyOverrides above was a no-op against an
      // empty cache).
      if (hasAnyCachedText()) {
        unhideDocumentForCms();
      }
    });
  }, [lang]);

  // Stop propagation when in edit mode so clicking a link/button while
  // editing doesn't trigger navigation. We use capture-phase listeners.
  //
  // For BUTTON / A / LABEL we also have to suppress the browser's
  // default mousedown behavior — those elements get "control focus"
  // rather than a text caret on click, so typing silently fails until
  // the user manually places a caret with the arrow keys. We focus the
  // element ourselves and place the caret at the click position.
  //
  // We do the focus + caret work on BOTH `mousedown` (so the focus
  // happens before any React onMouseDown side-effects) and `mouseup`
  // (in case a deferred React state update reverted focus between
  // mousedown and mouseup — common with the floating CMS toolbar's
  // focus retention). The work is idempotent.
  const focusAndPlaceCaret = useCallback((el, clientX, clientY) => {
    try {
      el.focus({ preventScroll: true });
    } catch {
      try { el.focus(); } catch { /* ignore */ }
    }
    try {
      let range = null;
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(clientX, clientY);
      } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(clientX, clientY);
        if (pos) {
          range = document.createRange();
          range.setStart(pos.offsetNode, pos.offset);
          range.collapse(true);
        }
      }
      if (!range || !el.contains(range.startContainer)) {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } catch {
      // Best-effort caret placement; native focus is already done.
    }
  }, []);

  const stopClick = useCallback((e) => {
    const el = e.currentTarget;
    if (!el || !el.isContentEditable) return;
    e.stopPropagation();
    if (e.type === "click") {
      e.preventDefault();
      return;
    }
    const tag = el.tagName;
    const isNativeControl = tag === "BUTTON" || tag === "A" || tag === "LABEL";
    if (e.type === "mousedown") {
      if (!isNativeControl) return;
      e.preventDefault();
      const { clientX, clientY } = e;
      focusAndPlaceCaret(el, clientX, clientY);
      // Defer a re-focus to the next microtask AND animation frame to
      // outlast any React re-renders that might steal focus back to a
      // sibling (e.g. the floating CMS toolbar). Cheap, idempotent.
      Promise.resolve().then(() => {
        if (document.activeElement !== el) focusAndPlaceCaret(el, clientX, clientY);
      });
      requestAnimationFrame(() => {
        if (document.activeElement !== el) focusAndPlaceCaret(el, clientX, clientY);
      });
      return;
    }
    if (e.type === "mouseup") {
      if (!isNativeControl) return;
      e.preventDefault();
      if (document.activeElement !== el) {
        focusAndPlaceCaret(el, e.clientX, e.clientY);
      }
      return;
    }
  }, [focusAndPlaceCaret]);

  const onPaste = useCallback((e) => {
    const el = e.currentTarget;
    if (!el?.isContentEditable) return;
    e.preventDefault();
    // Read the clipboard as plain text. Rich sources (Word / Docs /
    // Notion / ChatGPT) all set `text/plain` with newlines preserved.
    // We only normalise NBSPs and CRLF variants — real line breaks are
    // kept verbatim so paragraph structure survives the round-trip.
    const raw = e.clipboardData?.getData("text/plain") || "";
    if (!raw) return;
    const text = raw.replace(/\u00A0/g, " ").replace(/\r\n?/g, "\n");
    // Snapshot the pre-paste state so Ctrl+Z can undo THIS paste.
    ensureHistoryBaseline(el);
    // Perform the insertion via direct DOM mutation (skip execCommand
    // entirely — its behaviour with `contenteditable="plaintext-only"`
    // is inconsistent across Chrome versions: `\n` sometimes becomes
    // `<br>`, sometimes gets swallowed, sometimes splits the node into
    // sibling `<div>`s. Manual insertion of a single text node with
    // real `\n` characters + CSS `white-space: pre-line` is
    // deterministic in every engine).
    const sel = window.getSelection();
    if (!sel) return;
    let range = null;
    if (sel.rangeCount > 0) {
      range = sel.getRangeAt(0);
      // If the selection lives outside the current editable, ignore it
      // and place the caret at the end of the editable instead — the
      // browser sometimes preserves a stale selection from a prior
      // click that we must not use for insertion.
      if (!el.contains(range.startContainer)) {
        range = null;
      }
    }
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    // Move caret to end of inserted text.
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    // Update multiline flag based on the element's full text.
    setMultilineFlag(el, (el.innerText || "").includes("\n"));
    // Push the post-paste state as a new history entry — this is what
    // Ctrl+Z will roll back from (revealing the previous baseline).
    pushHistorySnapshot(el);
    // Manually dispatch an input event so `onInput` (and its debounced
    // `scheduleStage`) picks up the paste — direct DOM mutation does
    // NOT fire input events natively, unlike execCommand.
    el.dispatchEvent(new InputEvent("input", {
      bubbles: true,
      cancelable: true,
      inputType: "insertFromPaste",
    }));
  }, []);

  // Debounced staged-text writer used from onInput. Stages the edit
  // shortly after typing/pasting stops so React re-renders that fire
  // BEFORE onBlur (e.g. after admin galleries hydrate) can no longer
  // silently discard the operator's work. Kept in a ref so the timer
  // is shared across all editable elements without leaking closures.
  const stageDebounce = useRef(null);
  const scheduleStage = useCallback((el) => {
    if (!el || !el.dataset?.cmsAutoText) return;
    if (stageDebounce.current) clearTimeout(stageDebounce.current);
    stageDebounce.current = setTimeout(() => {
      const slot = el.dataset.cmsAutoText;
      const text = normalizeCmsText(el.innerText || "");
      const previous = normalizeCmsText(
        (getCachedText(slot) && (getCachedText(slot)[lang])) ||
        el.dataset.cmsText ||
        ""
      );
      if (text === previous) return;
      try {
        stageText(slot, { [lang]: text });
      } catch (err) {
        console.error("[CMS] auto-text stage failed", err);
      }
    }, 250);
  }, [lang]);

  // Debounced snapshot writer — coalesces bursts of keystrokes into a
  // single history entry so Ctrl+Z rolls back "a word / a phrase" at a
  // time (native text-editor UX) instead of one character at a time.
  const snapshotDebounce = useRef(null);
  const scheduleSnapshot = useCallback((el) => {
    if (!el) return;
    if (snapshotDebounce.current) clearTimeout(snapshotDebounce.current);
    snapshotDebounce.current = setTimeout(() => pushHistorySnapshot(el), 500);
  }, []);

  const onInput = useCallback((e) => {
    // Toggle the multiline flag live as the operator types Enter or
    // deletes the last line break, so the CSS `white-space: pre-line`
    // rule kicks in immediately and the caret stays on the correct
    // line while editing.
    const el = e.currentTarget;
    if (!el || !el.isContentEditable) return;
    setMultilineFlag(el, (el.innerText || "").includes("\n"));
    // Also stage on input so React re-renders that happen before blur
    // (async admin data, image gallery hydration, sibling context
    // updates) cannot silently wipe the operator's edit — the staged
    // cache holds the truth and `applyOverridesToRoot` re-paints it.
    scheduleStage(el);
    // Coalesced snapshot for Ctrl+Z / Ctrl+Y support.
    ensureHistoryBaseline(el);
    scheduleSnapshot(el);
  }, [scheduleStage, scheduleSnapshot]);

  const onKeyDown = useCallback((e) => {
    const el = e.currentTarget;
    if (!el?.isContentEditable) return;
    // Cmd/Ctrl + Enter = explicit commit. Bare Enter is now reserved
    // for inserting a newline, which is the natural behaviour on a
    // `contenteditable="plaintext-only"` element — indispensable for
    // multi-paragraph pastes / edits.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      el.blur();
      return;
    }
    // Undo / Redo — Ctrl/⌘+Z, Ctrl/⌘+Shift+Z (redo), Ctrl+Y (redo).
    // We roll our own history because native contentEditable undo
    // doesn't track our DOM-mutation-based paste implementation.
    // Flush any pending snapshot first so the current burst counts
    // toward the history before we walk it.
    const isMod = e.metaKey || e.ctrlKey;
    if (isMod && !e.altKey) {
      const k = e.key.toLowerCase();
      if (k === "z") {
        e.preventDefault();
        if (snapshotDebounce.current) {
          clearTimeout(snapshotDebounce.current);
          snapshotDebounce.current = null;
          pushHistorySnapshot(el);
        }
        const did = e.shiftKey ? redoHistory(el) : undoHistory(el);
        if (did) {
          el.dispatchEvent(new InputEvent("input", {
            bubbles: true,
            cancelable: true,
            inputType: e.shiftKey ? "historyRedo" : "historyUndo",
          }));
        }
        return;
      }
      if (k === "y") {
        e.preventDefault();
        if (snapshotDebounce.current) {
          clearTimeout(snapshotDebounce.current);
          snapshotDebounce.current = null;
          pushHistorySnapshot(el);
        }
        const did = redoHistory(el);
        if (did) {
          el.dispatchEvent(new InputEvent("input", {
            bubbles: true,
            cancelable: true,
            inputType: "historyRedo",
          }));
        }
        return;
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      const orig = el.dataset.cmsText || "";
      const slot = el.dataset.cmsAutoText;
      const cached = slot ? getCachedText(slot) : null;
      const reverted =
        (cached && (cached[lang] || cached.en || cached.fr || cached.es)) ||
        orig;
      // Reset content atomically to handle multi-line reverts. Uses
      // `setElementText` so element children (icons, decorative
      // images) survive the revert — see setElementText for details.
      setElementText(el, reverted);
      el.blur();
    }
  }, [lang]);

  // Establish a history baseline the first time an element is focused
  // in edit mode — this becomes the "state 0" that the very first
  // Ctrl+Z will roll back to.
  const onFocus = useCallback((e) => {
    const el = e.currentTarget;
    if (!el?.isContentEditable) return;
    ensureHistoryBaseline(el);
  }, []);

  const onBlur = useCallback(async (e) => {
    const el = e.currentTarget;
    if (!el || !el.dataset || !el.dataset.cmsAutoText) return;
    const slot = el.dataset.cmsAutoText;
    // Preserve newlines verbatim; only collapse horizontal noise.
    const text = normalizeCmsText(el.innerText || "");
    // Note: we deliberately do NOT short-circuit on empty `text` here.
    // A blank-on-blur is a real user intent ("clear this slot") that
    // must be staged just like any other edit; otherwise the override
    // pass downstream re-paints the original copy and the user thinks
    // delete is broken (see commit msg: "Edit Text: allow empty/blank
    // saves").
    const previous = normalizeCmsText(
      (getCachedText(slot) && (getCachedText(slot)[lang])) ||
      el.dataset.cmsText ||
      ""
    );
    if (text === previous) return;
    try {
      stageText(slot, { [lang]: text });
    } catch (err) {
      console.error("[CMS] auto-text stage failed", err);
    }
  }, [lang]);

  // Make every text candidate contentEditable while in edit mode; clean
  // up when exiting. Wires per-element listeners that persist on blur
  // and revert on Escape.
  const hydrateInteractivity = useCallback(() => {
    // ── Pre-pass: wrap orphaned text fragments ──────────────────────
    // Patterns like `<h3>Bivouacs <em>Xaluca</em></h3>` leave the
    // "Bivouacs" text as a bare text node — it has no element wrapper,
    // so AutoTextEditor cannot bind it as an editable leaf (and the
    // parent <h3> is rightly disqualified as having a nested text-tag
    // child). We patch the DOM once by wrapping every bare text-node
    // sibling of an inline-formatting child in a discrete <span>, so
    // both fragments become independent CMS slots. Without this step
    // the user could only edit the gold-accent fragment, never the
    // primary word — which is exactly the "Vivacs is not editable"
    // bug reported on the bivouacs hero.
    const INLINE_ACCENT_TAGS = new Set([
      "EM", "STRONG", "B", "I", "U", "S", "MARK",
    ]);
    const isFragmentParentCandidate = (parent) => {
      if (!parent || parent.nodeType !== 1) return false;
      if (!TEXT_TAGS.has(parent.tagName)) return false;
      if (isInsideManagedText(parent)) return false;
      if (isOptedOut(parent)) return false;
      if (isFormInput(parent)) return false;
      if (isOwnUi(parent)) return false;
      // Must contain at least one inline-accent child element AND at
      // least one direct, non-whitespace text node — that's the
      // signature of the bare-text-next-to-<em> pattern we're fixing.
      let hasAccent = false;
      let hasBareText = false;
      for (const child of parent.childNodes) {
        if (child.nodeType === 1 && INLINE_ACCENT_TAGS.has(child.tagName)) {
          hasAccent = true;
        } else if (child.nodeType === 3) {
          const t = (child.textContent || "").replace(/\s+/g, " ").trim();
          if (t) hasBareText = true;
        }
      }
      return hasAccent && hasBareText;
    };
    document
      .querySelectorAll(Array.from(TEXT_TAGS).join(","))
      .forEach((parent) => {
        if (!isFragmentParentCandidate(parent)) return;
        // Iterate childNodes by index defensively since we mutate.
        const nodes = Array.from(parent.childNodes);
        for (const node of nodes) {
          if (node.nodeType !== 3) continue;
          const raw = node.textContent || "";
          if (!raw.replace(/\s+/g, " ").trim()) continue;
          const span = document.createElement("span");
          span.dataset.cmsTextFragment = "1";
          span.textContent = raw;
          parent.replaceChild(span, node);
        }
      });

    // ── Cleanup pass ─────────────────────────────────────────────────
    // Runs unconditionally so that, even when textEditMode flips off,
    // we always strip listeners + contentEditable + the amber ring from
    // every previously-bound element. We must use the STATIC candidate
    // check here — `isTextCandidate` returns false for any element
    // whose `contentEditable` we set on the previous hydrate, which
    // would otherwise cause an endless strip-and-rebind cycle that
    // erases the user's input on every keystroke.
    document.querySelectorAll('[data-cms-bound="1"]').forEach((el) => {
      if (textEditMode && isStaticallyCandidate(el)) return; // still a valid bound candidate
      el.removeEventListener("blur", onBlur, true);
      el.removeEventListener("focus", onFocus, true);
      el.removeEventListener("keydown", onKeyDown, true);
      el.removeEventListener("paste", onPaste, true);
      el.removeEventListener("input", onInput, true);
      el.removeEventListener("click", stopClick, true);
      el.removeEventListener("mousedown", stopClick, true);
      el.removeEventListener("mouseup", stopClick, true);
      el.removeAttribute("contenteditable");
      el.classList.remove("editable-text-on");
      if (el.dataset.cmsAddedTabindex === "1") {
        el.removeAttribute("tabindex");
        delete el.dataset.cmsAddedTabindex;
      }
      delete el.dataset.cmsBound;
    });
    if (!textEditMode) return;

    // ── Bind pass ────────────────────────────────────────────────────
    const all = document.querySelectorAll(Array.from(TEXT_TAGS).join(","));
    all.forEach((el) => {
      // Use the static check so already-bound (and therefore
      // contentEditable) elements still qualify as candidates.
      if (!isStaticallyCandidate(el)) return;
      if (!el.dataset.cmsText) {
        el.dataset.cmsText = normalizeCmsText(el.textContent || "");
      }
      const slot = slotFromTextElement(el);
      if (!slot) return;
      el.dataset.cmsAutoText = slot;

      if (el.dataset.cmsBound !== "1") {
        el.dataset.cmsBound = "1";
        el.addEventListener("blur", onBlur, true);
        el.addEventListener("focus", onFocus, true);
        el.addEventListener("keydown", onKeyDown, true);
        el.addEventListener("paste", onPaste, true);
        el.addEventListener("input", onInput, true);
        el.addEventListener("click", stopClick, true);
        el.addEventListener("mousedown", stopClick, true);
        el.addEventListener("mouseup", stopClick, true);
      }
      if (!el.isContentEditable) {
        el.setAttribute("contenteditable", "plaintext-only");
        el.classList.add("editable-text-on");
      }
      // Sync the multiline flag with the initial content — needed so
      // the CSS `white-space: pre-line` rule kicks in immediately on
      // slots whose persisted value already contains newlines when the
      // operator enters Edit Text Mode.
      setMultilineFlag(el, (el.innerText || "").includes("\n"));
      // Some `<a>` elements (notably React Router `<Link>`s styled as
      // `inline-flex` wrapping a child SVG icon) refuse to accept focus
      // via `el.focus()` even though they have an href and are natively
      // focusable. Forcing `tabindex=0` makes them programmatically
      // focusable without changing keyboard tab order behaviour
      // meaningfully (the link was already in the tab order).
      // We track our injected tabindex so the cleanup pass can remove it.
      if ((el.tagName === "A" || el.tagName === "BUTTON" || el.tagName === "LABEL")
          && !el.hasAttribute("tabindex")) {
        el.setAttribute("tabindex", "0");
        el.dataset.cmsAddedTabindex = "1";
      }
      if (!subsRef.current.has(slot)) {
        const off = subscribeText(slot, () => tick());
        subsRef.current.set(slot, off);
      }
    });
  }, [textEditMode, tick, onBlur, onFocus, onKeyDown, onPaste, onInput, stopClick]);

  // Late-bind `hydrate` so `tick` (declared earlier) can call it without
  // creating a circular useCallback dependency.
  fnsRef.current.hydrate = hydrateInteractivity;

  // ── Initial load + slot-cache subscription ────────────────────────────
  // STEP 1 (synchronous, blocks paint): if the slot cache was hydrated
  // from localStorage on module load it already has the overrides for
  // this language. Apply them in a layout effect so the browser never
  // paints the hardcoded default text first ("FOUT" — Flash of
  // Untranslated Text). For brand-new visitors with no cache, this is a
  // no-op and the async fetch below provides the values after first
  // paint.
  //
  // NOTE: we do NOT call unhideDocumentForCms() here. The boot script
  // in index.html only hides the document when localStorage is empty
  // (true cold cache). In that case the cache is also empty at this
  // useLayoutEffect, applyOverrides() would be a no-op, and unhiding
  // here would defeat the entire single-source-of-truth guarantee.
  // The unhide happens inside `tick()` (called after the async fetch
  // resolves), AFTER applyOverrides has actually written CMS values.
  useLayoutEffect(() => {
    if (isAdminPath) return;
    applyOverrides(lang);
  }, [lang, isAdminPath]);

  // STEP 2 (async): refresh from server in the background.
  useEffect(() => {
    if (isAdminPath) return undefined;
    let active = true;
    ensureTextLoaded().then(() => {
      if (!active) return;
      tick();
    });
    return () => {
      active = false;
    };
  }, [tick, isAdminPath]);

  // ── Re-apply overrides + interactivity whenever language or edit mode change.
  useEffect(() => {
    if (isAdminPath) return;
    tick();
  }, [lang, textEditMode, tick, isAdminPath]);

  // ── MutationObserver: handles new sections + route changes.
  //
  // SYNCHRONOUS PAINT BARRIER: when React adds new DOM nodes (route
  // change, lazy section, modal opening, etc.) we apply CMS overrides
  // synchronously inside the observer callback — which runs as a
  // microtask BEFORE the browser paints the new nodes. This is the
  // critical hook that prevents hardcoded text from ever painting on
  // SPA navigation. The interactivity hydration (contentEditable
  // bindings, listeners) is still deferred to the next animation
  // frame via tick() since it doesn't affect first paint.
  useEffect(() => {
    if (isAdminPath) return undefined;
    const observer = new MutationObserver((mutations) => {
      let interesting = false;
      const addedRoots = new Set();
      for (const m of mutations) {
        if (m.type === "characterData") continue;
        if (m.type === "attributes" && m.attributeName === "contenteditable") continue;
        if (m.type === "attributes" && m.attributeName === "class") continue;
        if (m.type === "childList") {
          if (m.addedNodes.length === 0 && m.removedNodes.length === 0) continue;
          for (const node of m.addedNodes) {
            if (node.nodeType === 1) addedRoots.add(node);
          }
        }
        interesting = true;
      }
      // Apply overrides to JUST the newly-added subtrees, synchronously,
      // before the browser paints them. This eliminates flicker on SPA
      // route changes.
      if (addedRoots.size > 0) {
        for (const root of addedRoots) {
          applyOverridesToRoot(root, lang);
        }
        // Also clear cms-loading in case this was the very first paint
        // (cold-cache covered by tick() too, defense-in-depth).
        if (hasAnyCachedText()) unhideDocumentForCms();
      }
      if (interesting) tick();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-cms-text", "data-cms-text-slot"],
    });
    return () => observer.disconnect();
  }, [tick, isAdminPath, lang]);

  // ── Cleanup all subscriptions on unmount.
  useEffect(() => {
    const subs = subsRef.current;
    return () => {
      for (const off of subs.values()) off();
      subs.clear();
    };
  }, []);

  return null;
};

export default AutoTextEditor;
